/**
 * Custom React hooks for Task Brief API (Phase 0)
 *
 * These hooks provide access to the roleserver's task understanding endpoints,
 * enabling iterative task analysis before workflow decomposition.
 */

import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 minutes

/**
 * Poll GET /api/v1/workflows/{workflowId}/status until status != 'processing',
 * then fetch the full workflow graph and return it merged with initialData.
 */
async function pollWorkflowReady(workflowId, initialData) {
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

    const statusResp = await axios.get(`/api/v1/workflows/${workflowId}/status`);
    const { status, error_message } = statusResp.data;

    if (status === 'ready') {
      const graphResp = await axios.get(`/api/v1/workflows/${workflowId}`);
      return { ...initialData, ...graphResp.data };
    }

    if (status === 'failed') {
      throw new Error(error_message || 'Decomposition failed');
    }

    // status === 'processing' — keep polling
  }

  throw new Error('Decomposition timed out — please try again');
}

/**
 * Hook to approve a task brief and optionally proceed to decomposition.
 *
 * The approve endpoint now returns HTTP 202 immediately and runs decomposition
 * as a background job. This hook polls the workflow status until ready, then
 * returns the full graph — callers see no behavioural difference.
 *
 * @param {string} briefId - Task brief UUID
 * @returns {Object} Approval controls and state
 */
export function useTaskBriefApproval(briefId) {
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const approve = useCallback(async (proceedToDecomposition = true, inputValues = null, inputFormSchema = null) => {
    if (!briefId) {
      setError(new Error('briefId is required'));
      return null;
    }

    setApproving(true);
    setError(null);

    try {
      const body = { proceed_to_decomposition: proceedToDecomposition };
      if (inputValues && Object.keys(inputValues).length > 0) {
        body.input_values = inputValues;
      }
      if (inputFormSchema) {
        body.input_form_schema = inputFormSchema;
      }
      const response = await axios.post(
        `/api/v1/task-briefs/${briefId}/approve`,
        body
      );

      // 202: background job started — poll until the workflow is ready
      if (response.status === 202) {
        const { workflow_id } = response.data;
        const finalResult = await pollWorkflowReady(workflow_id, response.data);
        setResult(finalResult);
        return finalResult;
      }

      // 200: synchronous response (already-ready idempotent path or no decomposition)
      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Approval failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setApproving(false);
    }
  }, [briefId]);

  return {
    approving,
    error,
    result,
    approve
  };
}

/**
 * Hook to reject a task brief with feedback
 *
 * @param {string} briefId - Task brief UUID
 * @returns {Object} Rejection controls and state
 */
export function useTaskBriefRejection(briefId) {
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const reject = useCallback(async (feedback) => {
    if (!briefId) {
      setError(new Error('briefId is required'));
      return null;
    }

    if (!feedback || !feedback.trim()) {
      setError(new Error('Feedback is required'));
      return null;
    }

    setRejecting(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/v1/task-briefs/${briefId}/reject`,
        { feedback }
      );

      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Rejection failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setRejecting(false);
    }
  }, [briefId]);

  return {
    rejecting,
    error,
    result,
    reject
  };
}

/**
 * Hook to modify a task brief
 *
 * @param {string} briefId - Task brief UUID
 * @returns {Object} Modification controls and state
 */
export function useTaskBriefModification(briefId) {
  const [modifying, setModifying] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const modify = useCallback(async (modifications, userNotes = null) => {
    if (!briefId) {
      setError(new Error('briefId is required'));
      return null;
    }

    if (!modifications || Object.keys(modifications).length === 0) {
      setError(new Error('Modifications are required'));
      return null;
    }

    setModifying(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/v1/task-briefs/${briefId}/modify`,
        {
          modifications,
          user_notes: userNotes
        }
      );

      setResult(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Modification failed';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setModifying(false);
    }
  }, [briefId]);

  return {
    modifying,
    error,
    result,
    modify
  };
}

/**
 * Hook to fetch a task brief by ID
 *
 * @param {string} briefId - Task brief UUID
 * @param {boolean} autoFetch - Whether to auto-fetch on mount
 * @returns {Object} Task brief state and methods
 */
export function useTaskBriefDetails(briefId, autoFetch = false) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBrief = useCallback(async () => {
    if (!briefId) {
      setError(new Error('briefId is required'));
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/v1/task-briefs/${briefId}`);
      setBrief(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : err.response?.data?.detail?.message || err.message || 'Failed to fetch task brief';
      setError(new Error(errorMessage));
      return null;
    } finally {
      setLoading(false);
    }
  }, [briefId]);

  // Auto-fetch on mount if configured
  useEffect(() => {
    if (autoFetch && briefId) {
      fetchBrief();
    }
  }, [autoFetch, briefId, fetchBrief]);

  return {
    brief,
    loading,
    error,
    refetch: fetchBrief
  };
}
